import {
  HEADER_ICON,
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import React, { useState, useEffect } from "react";
import { useTranslation } from "../contexts/LanguageContext";
import {
  Mic,
  Video,
  Upload,
  Sparkles,
  Volume2,
  Play,
  Pause,
  Download,
  Check,
  Clock,
  AlertCircle,
  FileText,
  Film,
  RotateCcw,
  Image as ImageIcon,
  User as UserIcon,
} from "lucide-react";
import { Button, PageLayout } from "../components";
import apiService from "../shared/apiService";
import { getPostVideoUrl } from "../shared/constants";

interface Voice {
  voice_id: string;
  name: string;
  category: string;
  description?: string;
  preview_url?: string;
}

const VideoStudio: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"clone" | "produce">("produce");

  // Voice Cloning States
  const [voices, setVoices] = useState<Voice[]>([]);
  const [voiceName, setVoiceName] = useState("");
  const [voiceDescription, setVoiceDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneError, setCloneError] = useState("");
  const [cloneSuccess, setCloneSuccess] = useState("");
  // Vira `true` na primeira tentativa de envio com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [cloneAttemptedSubmit, setCloneAttemptedSubmit] = useState(false);

  // Voice Recording States
  const [recordMode, setRecordMode] = useState<"upload" | "mic">("upload");
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null,
  );
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [recordingTimer, setRecordingTimer] = useState<any>(null);

  // Video Production States
  const [scriptTitle, setScriptTitle] = useState(
    t("Como Hackear o Algoritmo do YouTube"),
  );
  const [scriptText, setScriptText] = useState(
    t(
      "Engajar seu público nos primeiros 5 segundos é o segredo absoluto de qualquer vídeo de sucesso no YouTube.\n\nA maioria dos criadores comete o erro de começar com vinhetas demoradas ou explicações longas. Em vez disso, comece jogando uma pergunta intrigante ou mostrando o resultado final imediatamente.\n\nDepois de capturar a atenção, entregue o conteúdo prometido de forma clara e ágil, dividindo em 3 tópicos práticos.\n\nPor fim, adicione uma chamada de ação natural e irresistível: peça para curtir e assistir ao próximo vídeo sugerido no card.",
    ),
  );
  const [selectedVoiceId, setSelectedVoiceId] = useState(
    "21m00Tcm4TlvDq8ikWAM",
  );
  const [videoMode, setVideoMode] = useState<
    "slide_purple" | "slide_custom_image" | "talking_avatar"
  >("slide_purple");
  const [customImage, setCustomImage] = useState<File | null>(null);
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [generationError, setGenerationError] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [, setAudioUrl] = useState("");
  const [videoDuration, setVideoDuration] = useState(0);
  const [attachPostId, setAttachPostId] = useState("");
  // Post ao qual o último vídeo compilado foi anexado (para feedback visual).
  const [attachedToPostId, setAttachedToPostId] = useState<number | null>(null);
  // Vira `true` na primeira tentativa de compilar com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [produceAttemptedSubmit, setProduceAttemptedSubmit] = useState(false);

  // Audio Previews
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null,
  );

  // Recording controls
  useEffect(() => {
    return () => {
      if (recordingTimer) clearInterval(recordingTimer);
    };
  }, [recordingTimer]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: "audio/mpeg" });
        const file = new File([audioBlob], `minha_gravacao_${Date.now()}.mp3`, {
          type: "audio/mpeg",
        });
        setSelectedFile(file);
        setRecordedAudioUrl(URL.createObjectURL(audioBlob));
        setCloneError("");
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      setRecordedAudioUrl(null);
      setSelectedFile(null);

      const timer = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      setCloneError("");
    } catch (err) {
      console.error("Erro ao acessar microfone:", err);
      setCloneError(
        t(
          "Não foi possível acessar o microfone. Verifique as permissões de gravação do navegador.",
        ),
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
    }
  };

  const formatRecordingTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins.toString().padStart(2, "0")}:${remainingSecs.toString().padStart(2, "0")}`;
  };

  // Load Voices
  const fetchVoices = async () => {
    try {
      const response = await apiService.get("/creator-benchmarks/voices");
      if (response && response.success) {
        setVoices(response.voices || []);
        // Set first voice as default if list is not empty
        if (response.voices && response.voices.length > 0 && !selectedVoiceId) {
          setSelectedVoiceId(response.voices[0].voice_id);
        }
      }
    } catch (err) {
      console.error("Erro ao buscar vozes ElevenLabs:", err);
    }
  };

  useEffect(() => {
    fetchVoices();
  }, []);

  // Play/Pause Voice Preview
  const handlePlayPreview = (voiceId: string, previewUrl?: string) => {
    if (!previewUrl) return;

    if (playingAudioId === voiceId) {
      audioElement?.pause();
      setPlayingAudioId(null);
      return;
    }

    if (audioElement) {
      audioElement.pause();
    }

    const audio = new Audio(previewUrl);
    audio.play();
    setAudioElement(audio);
    setPlayingAudioId(voiceId);

    audio.onended = () => {
      setPlayingAudioId(null);
    };
  };

  // Upload Voice File for Cloning
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setCloneError("");
    }
  };

  const handleCloneVoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setCloneAttemptedSubmit(true);
    if (!selectedFile) {
      setCloneError(t("Selecione um arquivo de áudio de amostra."));
      return;
    }
    if (!voiceName.trim()) {
      setCloneError(t("Insira um nome para a voz clonada."));
      return;
    }

    setIsCloning(true);
    setCloneError("");
    setCloneSuccess("");

    try {
      const response = await apiService.uploadFile(
        "/creator-benchmarks/voices/clone",
        selectedFile,
        {
          name: voiceName,
          description: voiceDescription,
        },
      );

      if (response && response.success) {
        setCloneSuccess(
          `${t("Voz")} "${voiceName}" ${t("clonada com sucesso!")}`,
        );
        setVoiceName("");
        setVoiceDescription("");
        setSelectedFile(null);
        setCloneAttemptedSubmit(false);
        // Refresh voices list
        await fetchVoices();
        // Select newly cloned voice
        if (response.voice_id) {
          setSelectedVoiceId(response.voice_id);
        }
      } else {
        setCloneError(response?.error || t("Falha ao clonar voz."));
      }
    } catch (err: any) {
      setCloneError(
        err?.message ||
          t("Ocorreu um erro ao enviar arquivo para o ElevenLabs."),
      );
    } finally {
      setIsCloning(false);
    }
  };

  // Deep Researcher Script Autofill
  const handleAutofillDeepScript = async () => {
    try {
      const response = await apiService.post(
        "/creator-benchmarks/generate-script-from-icp",
        {},
      );
      if (response && response.success && response.script) {
        setScriptTitle(
          response.title || t("Roteiro Otimizado de Alta Performance"),
        );
        setScriptText(response.script);
        return;
      }
    } catch (err) {
      console.warn(
        "Erro ao carregar roteiro dinâmico do ICP, usando fallback corporativo:",
        err,
      );
    }

    // Graceful fallback to a premium corporate/tech script if no records found or API fails
    setScriptTitle(t("O Segredo da Escala com IA"));
    setScriptText(
      t(
        "A maioria das empresas comete o erro de tentar escalar o atendimento e vendas contratando mais pessoas sem estruturar seus processos.\n\nA inteligência artificial generativa permite mapear o comportamento dos seus melhores clientes e automatizar interações com tom humano de forma previsível.\n\nPara começar a aplicar isso no seu negócio hoje, identifique o maior gargalo de tempo na sua equipe e configure um agente automatizado focado em resolver esse problema.\n\nSe você quer otimizar a operação da sua empresa e aumentar em até 3x a eficiência com base em dados de mercado, clique no link da bio para testar nossa demonstração gratuita.",
      ),
    );
  };

  // Compile Video (async job + polling)
  const pollStudioVideoJob = async (jobId: string) => {
    const maxAttempts = 200;
    const intervalMs = 3000;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const statusRes = await apiService.get(
        `/creator-benchmarks/generate-video/status/${jobId}`,
      );
      const status = statusRes?.status;
      if (status === "processing") {
        setGenerationStep(2);
      } else if (status === "pending") {
        setGenerationStep(1);
      }

      if (status === "completed") {
        return statusRes;
      }
      if (status === "failed") {
        throw new Error(
          statusRes?.error || statusRes?.message || t("Erro ao gerar vídeo."),
        );
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    throw new Error(t("Tempo esgotado aguardando a compilação do vídeo."));
  };

  const handleCompileVideo = async () => {
    setProduceAttemptedSubmit(true);
    if (!scriptText.trim()) return;
    if (videoMode !== "slide_purple" && !customImage) return;

    setIsGenerating(true);
    setGenerationError("");
    setVideoUrl("");
    setAudioUrl("");
    setAttachedToPostId(null);
    setGenerationStep(1);

    try {
      const formData = new FormData();
      formData.append("title", scriptTitle);
      formData.append("text", scriptText);
      formData.append("voice_id", selectedVoiceId);
      formData.append("video_mode", videoMode);

      const parsedPostId = parseInt(attachPostId.trim(), 10);
      const willAttachToPost = !Number.isNaN(parsedPostId) && parsedPostId > 0;
      if (willAttachToPost) {
        formData.append("post_id", String(parsedPostId));
      }

      if (
        customImage &&
        (videoMode === "slide_custom_image" || videoMode === "talking_avatar")
      ) {
        formData.append("image", customImage);
      }

      const enqueueRes = await apiService.post(
        "/creator-benchmarks/generate-video",
        formData,
        { timeout: 120000 },
      );

      const jobId = enqueueRes?.job_id || enqueueRes?.task_id;
      if (!jobId) {
        throw new Error(
          enqueueRes?.error || t("Resposta inválida ao enfileirar vídeo."),
        );
      }

      setGenerationStep(2);
      const response = await pollStudioVideoJob(jobId);

      setGenerationStep(3);
      const url = getPostVideoUrl(
        response.video_info || {
          public_url: response.video_url,
        },
      );
      setVideoUrl(url);
      setAudioUrl(response.audio_url || "");
      setVideoDuration(response.duration);
      setAttachedToPostId(willAttachToPost ? parsedPostId : null);
      setIsGenerating(false);
      setGenerationStep(0);
      setProduceAttemptedSubmit(false);
    } catch (err: any) {
      setGenerationError(
        err?.message || t("Erro na conexão com o servidor de renderização."),
      );
      setIsGenerating(false);
    }
  };

  return (
    <PageLayout
      subtitle={t(
        "Clone sua voz e compile roteiros em vídeos com IA — gratuito, sem consumir créditos.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2 py-2"
      actions={
        // São abas (não ações): a ativa usa o estilo primário, a inativa o secundário.
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setActiveTab("produce")}
            variant={activeTab === "produce" ? "primary" : "outline"}
            size="sm"
            aria-pressed={activeTab === "produce"}
            icon={
              <Video
                className={
                  activeTab === "produce" ? HEADER_ICON : HEADER_SECONDARY_ICON
                }
              />
            }
            className={
              activeTab === "produce"
                ? HEADER_PRIMARY_BUTTON
                : HEADER_SECONDARY_BUTTON
            }
          >
            <span className="hidden sm:inline text-xs">
              {t("Produzir Vídeos")}
            </span>
            <span className="sm:hidden text-xs">{t("Produzir")}</span>
          </Button>
          <Button
            onClick={() => setActiveTab("clone")}
            variant={activeTab === "clone" ? "primary" : "outline"}
            size="sm"
            aria-pressed={activeTab === "clone"}
            icon={
              <Mic
                className={
                  activeTab === "clone" ? HEADER_ICON : HEADER_SECONDARY_ICON
                }
              />
            }
            className={
              activeTab === "clone"
                ? HEADER_PRIMARY_BUTTON
                : HEADER_SECONDARY_BUTTON
            }
          >
            <span className="hidden sm:inline text-xs">{t("Clonar Voz")}</span>
            <span className="sm:hidden text-xs">{t("Voz")}</span>
          </Button>
        </div>
      }
    >
      {/* Main Workspace Layout */}
      {activeTab === "produce" ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left Column: Script Composer */}
          <div className="lg:col-span-7 bg-white rounded-2xl p-6 shadow-xs border border-purple-100 flex flex-col gap-6">
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-bold text-gray-800">
                  {t("Compositor de Roteiro")}
                </h2>
              </div>
              <button
                onClick={handleAutofillDeepScript}
                className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {t("Importar Roteiro do Deep Researcher")}
              </button>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  {t("Título do Roteiro / Tema")}
                </label>
                <span className="text-[10px] font-bold text-gray-400 tabular-nums">
                  {t("{count} caracteres", { count: scriptTitle.length })}
                </span>
              </div>
              <input
                type="text"
                value={scriptTitle}
                onChange={(e) => setScriptTitle(e.target.value)}
                className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-200 focus:outline-hidden focus:ring-2 focus:ring-purple-400 transition-all font-medium text-gray-800"
                placeholder={t("Ex: Como Hackear o Algoritmo")}
              />
            </div>

            <div className="flex flex-col gap-1 flex-1">
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  {t("Roteiro Completo (Parágrafos = Slides)")}{" "}
                  <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 font-medium">
                    {t("Cada parágrafo vira um slide")}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 tabular-nums">
                    {t("{count} caracteres", { count: scriptText.length })}
                  </span>
                </div>
              </div>
              <textarea
                value={scriptText}
                onChange={(e) => setScriptText(e.target.value)}
                rows={10}
                className={`w-full flex-1 px-3.5 py-2 text-sm rounded-xl border focus:outline-hidden focus:ring-2 focus:ring-purple-400 transition-all font-medium text-gray-700 leading-relaxed resize-none ${
                  produceAttemptedSubmit && !scriptText.trim()
                    ? "border-red-300"
                    : "border-gray-200"
                }`}
                placeholder={t("Escreva seu roteiro de vídeo aqui...")}
              />
              {produceAttemptedSubmit && !scriptText.trim() && (
                <p className="flex items-center gap-1 text-xs text-red-600 font-medium">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {t("O roteiro é obrigatório")}
                </p>
              )}
            </div>

            {/* Voice Picker */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t("Selecione a Voz para Narração")}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto pr-1">
                {voices.map((v) => (
                  <div
                    key={v.voice_id}
                    onClick={() => setSelectedVoiceId(v.voice_id)}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      selectedVoiceId === v.voice_id
                        ? "border-purple-600 bg-purple-50/50 shadow-xs"
                        : "border-gray-100 hover:border-purple-200 hover:bg-gray-50/50"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className={`p-2 rounded-lg ${
                          selectedVoiceId === v.voice_id
                            ? "bg-purple-600 text-white"
                            : "bg-purple-100 text-purple-600"
                        }`}
                      >
                        <Volume2 className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-800 truncate">
                          {v.name}
                        </p>
                        <p className="text-[10px] text-gray-400 capitalize truncate">
                          {v.category}
                        </p>
                      </div>
                    </div>
                    {v.preview_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayPreview(v.voice_id, v.preview_url);
                        }}
                        className="p-1 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-all"
                      >
                        {playingAudioId === v.voice_id ? (
                          <Pause className="w-3.5 h-3.5 text-purple-600" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modo de Vídeo */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t("Modo de Vídeo")}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div
                  onClick={() => setVideoMode("slide_purple")}
                  className={`flex flex-col items-center text-center p-3 rounded-xl border cursor-pointer transition-all ${
                    videoMode === "slide_purple"
                      ? "border-purple-600 bg-purple-50/50 shadow-xs"
                      : "border-gray-100 hover:border-purple-200 hover:bg-gray-50/50"
                  }`}
                >
                  <div
                    className={`p-2 rounded-lg mb-1.5 ${
                      videoMode === "slide_purple"
                        ? "bg-purple-600 text-white"
                        : "bg-purple-100 text-purple-600"
                    }`}
                  >
                    <Film className="w-4 h-4" />
                  </div>
                  <p className="text-xs font-bold text-gray-800">
                    {t("Fundo Roxo")}
                  </p>
                  <p className="text-[9px] text-gray-400 mt-0.5 leading-tight">
                    {t("Slides estéticos com gradiente premium")}
                  </p>
                </div>

                <div
                  onClick={() => setVideoMode("slide_custom_image")}
                  className={`flex flex-col items-center text-center p-3 rounded-xl border cursor-pointer transition-all ${
                    videoMode === "slide_custom_image"
                      ? "border-purple-600 bg-purple-50/50 shadow-xs"
                      : "border-gray-100 hover:border-purple-200 hover:bg-gray-50/50"
                  }`}
                >
                  <div
                    className={`p-2 rounded-lg mb-1.5 ${
                      videoMode === "slide_custom_image"
                        ? "bg-purple-600 text-white"
                        : "bg-purple-100 text-purple-600"
                    }`}
                  >
                    <ImageIcon className="w-4 h-4" />
                  </div>
                  <p className="text-xs font-bold text-gray-800">
                    {t("Sua Imagem")}
                  </p>
                  <p className="text-[9px] text-gray-400 mt-0.5 leading-tight">
                    {t("Fundo personalizado em widescreen")}
                  </p>
                </div>

                <div
                  onClick={() => setVideoMode("talking_avatar")}
                  className={`flex flex-col items-center text-center p-3 rounded-xl border cursor-pointer transition-all ${
                    videoMode === "talking_avatar"
                      ? "border-purple-600 bg-purple-50/50 shadow-xs"
                      : "border-gray-100 hover:border-purple-200 hover:bg-gray-50/50"
                  }`}
                >
                  <div
                    className={`p-2 rounded-lg mb-1.5 ${
                      videoMode === "talking_avatar"
                        ? "bg-purple-600 text-white"
                        : "bg-purple-100 text-purple-600"
                    }`}
                  >
                    <UserIcon className="w-4 h-4" />
                  </div>
                  <p className="text-xs font-bold text-gray-800">
                    {t("Avatar Falante")}
                  </p>
                  <p className="text-[9px] text-gray-400 mt-0.5 leading-tight">
                    {t("Foto animada de rosto com narração de IA")}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t("Anexar ao post (opcional)")}
              </label>
              <input
                type="number"
                min={1}
                value={attachPostId}
                onChange={(e) => setAttachPostId(e.target.value)}
                placeholder={t("ID do post para salvar o vídeo gerado")}
                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:outline-hidden focus:ring-2 focus:ring-purple-400 text-sm"
              />
              <p className="text-[10px] text-gray-400">
                {t(
                  "Se informado, o vídeo compilado será anexado ao post após a geração.",
                )}
              </p>
            </div>

            {/* Upload de Imagem Personalizada */}
            {(videoMode === "slide_custom_image" ||
              videoMode === "talking_avatar") && (
              <div
                className={`flex flex-col gap-2 p-3 border rounded-xl ${
                  produceAttemptedSubmit && !customImage
                    ? "bg-red-50/30 border-red-200"
                    : "bg-purple-50/30 border-purple-100"
                }`}
              >
                <label className="text-xs font-bold text-purple-950 uppercase tracking-wider flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5 text-purple-600" />
                  {videoMode === "talking_avatar"
                    ? t("Sua Foto de Rosto (Avatar)")
                    : t("Imagem de Fundo Personalizada")}{" "}
                  <span className="text-red-400 normal-case">*</span>
                </label>
                <div className="flex gap-4 items-center">
                  <div
                    className={`relative group border-2 border-dashed hover:border-purple-400 hover:bg-purple-50/50 rounded-xl p-4 transition-all flex flex-col items-center justify-center text-center cursor-pointer flex-1 min-h-[90px] ${
                      produceAttemptedSubmit && !customImage
                        ? "border-red-300"
                        : "border-purple-200"
                    }`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          setCustomImage(file);
                          setCustomImagePreview(URL.createObjectURL(file));
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <Upload className="w-4 h-4 text-purple-600 mb-1 group-hover:scale-110 transition-transform" />
                    <p className="text-xs font-bold text-gray-700">
                      {customImage
                        ? customImage.name
                        : t("Clique para selecionar foto")}
                    </p>
                    <p className="text-[9px] text-gray-400 mt-0.5">
                      {videoMode === "talking_avatar"
                        ? t("Envie uma foto de frente (fundo limpo)")
                        : t("Envie imagem HD (Widescreen 16:9 ideal)")}
                    </p>
                  </div>

                  {customImagePreview && (
                    <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-purple-200 shadow-xs shrink-0">
                      <img
                        src={customImagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCustomImage(null);
                          setCustomImagePreview(null);
                        }}
                        className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold"
                      >
                        {t("Remover")}
                      </button>
                    </div>
                  )}
                </div>
                {produceAttemptedSubmit && !customImage && (
                  <p className="flex items-center gap-1 text-xs text-red-600 font-medium">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {t("Envie uma imagem para continuar")}
                  </p>
                )}
              </div>
            )}

            <button
              onClick={handleCompileVideo}
              disabled={
                isGenerating ||
                !scriptText.trim() ||
                (videoMode !== "slide_purple" && !customImage)
              }
              className="w-full bg-linear-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-purple-600/20 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2.5 text-base mt-2"
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
              {videoMode !== "slide_purple" && !customImage
                ? t("Suba uma imagem para compilar o vídeo")
                : t("Sintetizar & Compilar Vídeo MP4")}
            </button>
          </div>

          {/* Right Column: Rendering & Output player */}
          <div className="lg:col-span-5 bg-white rounded-2xl p-6 shadow-xs border border-purple-100 flex flex-col gap-6">
            <div className="flex items-center gap-2 pb-4 border-b border-gray-100">
              <Film className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-800">
                {t("Visualização do Estúdio")}
              </h2>
            </div>

            {/* Rendering Progress Workspace */}
            {isGenerating && (
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-purple-50/40 rounded-2xl border border-dashed border-purple-200 min-h-[300px]">
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center animate-bounce mb-6 shadow-lg shadow-purple-600/30">
                  <Film className="w-8 h-8" />
                </div>
                <h3 className="font-extrabold text-gray-800 text-lg mb-2 text-center">
                  {t("Criando seu Vídeo Viral...")}
                </h3>
                <p className="text-gray-500 text-xs text-center max-w-xs mb-8 leading-relaxed">
                  {t(
                    "Por favor, aguarde alguns segundos. O FFmpeg está compilando suas fontes e áudio de voz clonada de forma otimizada.",
                  )}
                </p>

                {/* Vertical Process Steps */}
                <div className="w-full max-w-sm flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        generationStep >= 1
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {generationStep > 1 ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        "1"
                      )}
                    </div>
                    <span
                      className={`text-xs font-bold ${generationStep >= 1 ? "text-purple-900" : "text-gray-400"}`}
                    >
                      {t("Sintetizando Narração ElevenLabs")}
                    </span>
                    {generationStep === 1 && (
                      <Clock className="w-3.5 h-3.5 text-purple-600 animate-spin ml-auto" />
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        generationStep >= 2
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {generationStep > 2 ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        "2"
                      )}
                    </div>
                    <span
                      className={`text-xs font-bold ${generationStep >= 2 ? "text-purple-900" : "text-gray-400"}`}
                    >
                      {t("Gerando Slides de Alta Definição (Pillow)")}
                    </span>
                    {generationStep === 2 && (
                      <Clock className="w-3.5 h-3.5 text-purple-600 animate-spin ml-auto" />
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        generationStep >= 3
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {generationStep > 3 ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        "3"
                      )}
                    </div>
                    <span
                      className={`text-xs font-bold ${generationStep >= 3 ? "text-purple-900" : "text-gray-400"}`}
                    >
                      {t("Mesclando Streams e Renderizando MP4 (FFmpeg)")}
                    </span>
                    {generationStep === 3 && (
                      <Clock className="w-3.5 h-3.5 text-purple-600 animate-spin ml-auto" />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {generationError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-red-800">
                    {t("Falha na Compilação")}
                  </h4>
                  <p className="text-xs text-red-600 mt-1 leading-relaxed">
                    {generationError}
                  </p>
                </div>
              </div>
            )}

            {/* Empty State / Standby */}
            {!isGenerating && !videoUrl && !generationError && (
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 min-h-[300px] text-center">
                <Film className="w-12 h-12 text-gray-300 mb-4" />
                <h3 className="font-bold text-gray-700 text-sm mb-1">
                  {t("Seu Vídeo Aparecerá Aqui")}
                </h3>
                <p className="text-gray-400 text-xs max-w-xs leading-relaxed">
                  {t(
                    "Insira seu roteiro, selecione sua voz de clone ou pré-definida e compile para criar um vídeo incrível.",
                  )}
                </p>
              </div>
            )}

            {/* Output Widescreen Video Player */}
            {videoUrl && !isGenerating && (
              <div className="flex-1 flex flex-col gap-4">
                <div className="relative aspect-video rounded-2xl overflow-hidden shadow-lg border border-purple-200 bg-black">
                  <video
                    src={videoUrl}
                    controls
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100 flex flex-col gap-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-purple-900 uppercase">
                      {t("Resumo da Geração")}
                    </span>
                    <span className="bg-purple-200 text-purple-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {t("COMPILADO")}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-xs font-medium text-gray-600">
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">
                        {t("Duração Estimada")}
                      </p>
                      <p className="text-gray-800 mt-0.5 font-semibold">
                        {videoDuration.toFixed(1)} {t("segundos")}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">
                        {t("Formato de Saída")}
                      </p>
                      <p className="text-gray-800 mt-0.5 font-semibold">
                        {t("Widescreen MP4 (HD)")}
                      </p>
                    </div>
                  </div>

                  {attachedToPostId !== null && (
                    <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                      <Check className="w-3.5 h-3.5 shrink-0" />
                      {t("Vídeo anexado ao post #{id}", {
                        id: attachedToPostId,
                      })}
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <a
                    href={videoUrl}
                    download={`video_virtus_${Date.now()}.mp4`}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-xl shadow-md shadow-green-600/10 flex items-center justify-center gap-2 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    {t("Baixar Vídeo (MP4)")}
                  </a>
                  <button
                    onClick={() => {
                      setVideoUrl("");
                      setAudioUrl("");
                      setAttachedToPostId(null);
                    }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold p-3 rounded-xl border border-gray-200 flex items-center justify-center transition-all"
                    title={t("Recomeçar")}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Voice Cloning Workspace Tab */
        <div className="flex flex-col gap-4 w-full">
          {/* Sample Voice Uploader */}
          <div className="w-full bg-white rounded-2xl p-6 shadow-xs border border-purple-100 flex flex-col gap-6">
            <div className="flex items-center gap-2 pb-4 border-b border-gray-100">
              <Mic className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-800">
                {t("Registrar Nova Amostra de Voz")}
              </h2>
            </div>

            <form onSubmit={handleCloneVoice} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {t("Nome do Clone")} <span className="text-red-400">*</span>
                  </label>
                  <span className="text-[10px] font-bold text-gray-400 tabular-nums">
                    {t("{count} caracteres", { count: voiceName.length })}
                  </span>
                </div>
                <input
                  type="text"
                  value={voiceName}
                  onChange={(e) => setVoiceName(e.target.value)}
                  className={`w-full px-3.5 py-2 text-sm rounded-xl border focus:outline-hidden focus:ring-2 focus:ring-purple-400 transition-all font-medium text-gray-800 ${
                    cloneAttemptedSubmit && !voiceName.trim()
                      ? "border-red-300"
                      : "border-gray-200"
                  }`}
                  placeholder={t("Ex: Minha Voz Principal, Voz Diretor")}
                />
                {cloneAttemptedSubmit && !voiceName.trim() && (
                  <p className="flex items-center gap-1 text-xs text-red-600 font-medium">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {t("O nome do clone é obrigatório")}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {t("Descrição (opcional)")}
                  </label>
                  <span className="text-[10px] font-bold text-gray-400 tabular-nums">
                    {t("{count} caracteres", {
                      count: voiceDescription.length,
                    })}
                  </span>
                </div>
                <textarea
                  value={voiceDescription}
                  onChange={(e) => setVoiceDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-200 focus:outline-hidden focus:ring-2 focus:ring-purple-400 transition-all font-medium text-gray-700 resize-none"
                  placeholder={t(
                    "Ex: Voz calma, ideal para tutoriais e vídeos longos.",
                  )}
                />
              </div>

              {/* Toggle upload vs microphone */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => {
                    setRecordMode("upload");
                    setSelectedFile(null);
                    setRecordedAudioUrl(null);
                  }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold rounded-lg transition-all ${
                    recordMode === "upload"
                      ? "bg-white text-purple-950 shadow-xs"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Upload className="w-3.5 h-3.5" />
                  {t("Enviar Arquivo")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRecordMode("mic");
                    setSelectedFile(null);
                    setRecordedAudioUrl(null);
                  }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold rounded-lg transition-all ${
                    recordMode === "mic"
                      ? "bg-white text-purple-950 shadow-xs"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Mic className="w-3.5 h-3.5" />
                  {t("Gravar no Microfone")}
                </button>
              </div>

              {/* Input Area based on chosen mode */}
              {recordMode === "upload" ? (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {t("Amostra de Áudio (Arquivo)")}{" "}
                    <span className="text-red-400">*</span>
                  </label>
                  <div
                    className={`relative group border-2 border-dashed hover:border-purple-400 hover:bg-purple-50/20 rounded-2xl p-6 transition-all flex flex-col items-center justify-center text-center cursor-pointer min-h-[140px] ${
                      cloneAttemptedSubmit && !selectedFile
                        ? "border-red-300 bg-red-50/20"
                        : "border-gray-200"
                    }`}
                  >
                    <input
                      type="file"
                      accept="audio/mpeg,audio/wav,audio/x-m4a,audio/mp3"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />

                    <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center text-purple-600 mb-2 group-hover:scale-110 transition-transform">
                      <Upload className="w-5 h-5" />
                    </div>

                    <p className="text-xs font-bold text-gray-700">
                      {selectedFile
                        ? selectedFile.name
                        : t("Clique para selecionar ou arraste o arquivo")}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {t(
                        "Envie uma gravação de 1-2 minutos de você falando (MP3, WAV ou M4A) para clonagem perfeita.",
                      )}
                    </p>
                  </div>
                  {cloneAttemptedSubmit && !selectedFile && (
                    <p className="flex items-center gap-1 text-xs text-red-600 font-medium">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      {t("Selecione um arquivo de áudio")}
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {t("Microfone Gravador")}{" "}
                    <span className="text-red-400">*</span>
                  </label>
                  <div
                    className={`border rounded-2xl p-5 flex flex-col items-center justify-center text-center gap-4 ${
                      cloneAttemptedSubmit && !selectedFile
                        ? "border-red-300 bg-red-50/20"
                        : "border-purple-100 bg-purple-50/20"
                    }`}
                  >
                    {isRecording ? (
                      <div className="flex flex-col items-center gap-3">
                        {/* Pulse animation for premium aesthetic */}
                        <div className="relative flex items-center justify-center">
                          <span className="animate-ping absolute inline-flex h-12 w-12 rounded-full bg-red-400 opacity-75"></span>
                          <button
                            type="button"
                            onClick={stopRecording}
                            className="relative w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-red-600/30 hover:scale-105 transition-transform"
                          >
                            <span className="w-4 h-4 bg-white rounded-xs" />
                          </button>
                        </div>
                        <p className="text-xs font-black text-red-600 uppercase tracking-widest animate-pulse flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-600 inline-block animate-ping" />
                          {t("Gravando...")}
                        </p>
                        <p className="text-2xl font-black text-purple-950 font-mono">
                          {formatRecordingTime(recordingTime)}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {t(
                            "Fale de forma clara por 1-2 minutos. Clique no quadrado vermelho para parar.",
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3 w-full">
                        <button
                          type="button"
                          onClick={startRecording}
                          className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-purple-600/30 hover:scale-105 hover:bg-purple-700 transition-all"
                        >
                          <Mic className="w-5 h-5" />
                        </button>

                        <p className="text-xs font-bold text-gray-700">
                          {selectedFile
                            ? `✓ ${t("Nova Gravação Capturada!")} (${selectedFile.name})`
                            : t("Clique para Iniciar Gravação")}
                        </p>

                        {recordedAudioUrl && (
                          <div className="w-full flex flex-col gap-2 bg-white rounded-xl p-3 border border-purple-100">
                            <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider text-left">
                              {t("Prévia da Amostra Gravada:")}
                            </span>
                            <audio
                              src={recordedAudioUrl}
                              controls
                              className="w-full"
                            />
                          </div>
                        )}

                        <p className="text-[10px] text-gray-400 leading-relaxed">
                          {t(
                            "Recomenda-se gravar ao menos 30 segundos falando em tom normal para obter um clone realista.",
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                  {cloneAttemptedSubmit && !selectedFile && (
                    <p className="flex items-center gap-1 text-xs text-red-600 font-medium">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      {t("Grave uma amostra de áudio")}
                    </p>
                  )}
                </div>
              )}

              {/* Error & Success States */}
              {cloneError && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600 leading-relaxed">
                    {cloneError}
                  </p>
                </div>
              )}
              {cloneSuccess && (
                <div className="p-3.5 bg-green-50 border border-green-200 rounded-xl flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700 leading-relaxed">
                    {cloneSuccess}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isCloning || !selectedFile || !voiceName.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-xl disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              >
                {isCloning ? (
                  <>
                    <Clock className="w-4 h-4 animate-spin" />
                    {t("Enviando Voz para Clonagem...")}
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4" />
                    {t("Iniciar Clonagem de Voz")}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Active Voices Directory */}
          <div className="w-full bg-white rounded-2xl p-6 shadow-xs border border-purple-100 flex flex-col gap-6">
            <div className="flex items-center gap-2 pb-4 border-b border-gray-100">
              <Volume2 className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-800">
                {t("Diretório de Vozes de Criação")}
              </h2>
            </div>

            <div className="flex flex-col gap-3 flex-1 overflow-y-auto max-h-[460px] pr-1">
              {voices.map((v) => (
                <div
                  key={v.voice_id}
                  className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50/30 hover:border-purple-200 hover:bg-purple-50/10 transition-all"
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-extrabold text-sm uppercase">
                      {v.name.slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-extrabold text-gray-800">
                          {v.name}
                        </p>
                        <span
                          className={`text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase ${
                            v.category === "cloned" || v.category === "custom"
                              ? "bg-purple-600 text-white"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {v.category === "cloned" || v.category === "custom"
                            ? t("Clone")
                            : t("Padrão")}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-400 truncate mt-0.5">
                        {v.description || t("Sem descrição disponível.")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {v.preview_url && (
                      <button
                        onClick={() =>
                          handlePlayPreview(v.voice_id, v.preview_url)
                        }
                        className="p-2 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-white border border-gray-100 transition-all shadow-xs"
                        title={t("Ouvir Amostra")}
                      >
                        {playingAudioId === v.voice_id ? (
                          <Pause className="w-4 h-4 text-purple-600 animate-pulse" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {selectedVoiceId === v.voice_id ? (
                      <span className="bg-green-100 text-green-700 text-[10px] font-extrabold px-2.5 py-1.5 rounded-lg flex items-center gap-1">
                        <Check className="w-3.5 h-3.5" />
                        {t("Ativa")}
                      </span>
                    ) : (
                      <button
                        onClick={() => setSelectedVoiceId(v.voice_id)}
                        className="text-xs font-bold text-purple-600 hover:text-purple-700 px-2.5 py-1.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-all"
                      >
                        {t("Ativar")}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {voices.length === 0 && (
                <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 text-center">
                  <Volume2 className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-xs font-bold text-gray-500">
                    {t("Nenhuma Voz Carregada")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default VideoStudio;
